import {
  formatCompactINR,
  formatCompactMoney,
  formatINR,
  formatMoney,
  toInrMinor,
  type Currency,
} from '@fpc/shared';
import { useAuth, type CompanyMoney, type LiveInrRates, type ViewIn } from './useAuth';

export interface MoneyOptions {
  /** The amount's own currency. Defaults to the selected company's. */
  currency?: Currency | string;
}

export interface Money {
  /** The amount as the "View in" choice asks: own currency, or `≈ ₹` rupees. */
  format(minor: number, options?: MoneyOptions): string;
  compact(minor: number, options?: MoneyOptions): string;
  /** Always the amount in its own currency, whatever the view. */
  native(minor: number, options?: MoneyOptions): string;
  /** The other view's text, for a tooltip; undefined when there is no other view. */
  alternate(minor: number, options?: MoneyOptions): string | undefined;
  company: CompanyMoney;
  viewIn: ViewIn;
  setViewIn(mode: ViewIn): void;
  /** The live rates in use, when "View in INR" is on and they have arrived. */
  inrRates?: LiveInrRates;
}

const HOME: CompanyMoney = { currency: 'INR', country: 'IN', india: true, tds: false };

interface AuthMoney {
  company: CompanyMoney;
  viewIn: ViewIn;
  setViewIn(mode: ViewIn): void;
  inrRates?: LiveInrRates;
}

function useAuthMoney(): AuthMoney {
  try {
    const auth = useAuth();
    return {
      company: auth.company ?? HOME,
      viewIn: auth.viewIn ?? 'NATIVE',
      setViewIn: auth.setViewIn ?? (() => undefined),
      inrRates: auth.inrRates,
    };
  } catch {
    // Rendered outside an AuthProvider (an isolated component test): rupees.
    return { company: HOME, viewIn: 'NATIVE', setViewIn: () => undefined };
  }
}

/**
 * Money display for the selected company.
 *
 * Every amount a company owns is in its own currency; the common "View in"
 * dropdown can show them as rupees instead, converted at the latest rate
 * fetched live when the view is switched on (never stored) and marked `≈`
 * because it is an estimate. Until the rate arrives, or if it cannot be had,
 * an amount stays in its own currency. Display only: inputs, bank files and
 * matching always use the real currency.
 */
export function useMoney(): Money {
  const { company, viewIn, setViewIn, inrRates } = useAuthMoney();

  const currencyOf = (options: MoneyOptions = {}) =>
    (options.currency ?? company.currency) as Currency;

  const asInr = (minor: number, options: MoneyOptions | undefined, compact: boolean) => {
    const currency = currencyOf(options);
    const rate = inrRates?.rates[currency];
    if (currency === 'INR' || !rate) return undefined;
    const rupees = toInrMinor(minor, rate);
    return `≈ ${compact ? formatCompactINR(rupees) : formatINR(rupees)}`;
  };

  const native = (minor: number, options?: MoneyOptions) => formatMoney(minor, currencyOf(options));

  return {
    company,
    viewIn,
    setViewIn,
    inrRates,
    native,
    format: (minor, options) =>
      (viewIn === 'INR' && asInr(minor, options, false)) || native(minor, options),
    compact: (minor, options) =>
      (viewIn === 'INR' && asInr(minor, options, true)) ||
      formatCompactMoney(minor, currencyOf(options)),
    alternate: (minor, options) => {
      const inr = asInr(minor, options, false);
      if (!inr) return undefined;
      return viewIn === 'INR' ? native(minor, options) : inr;
    },
  };
}
